import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  workspacesApi,
  WorkspaceFilters,
  ProjectFilters,
  MemberFilters,
  CreateWorkspacePayload,
} from '@/api/workspaces';
import { organizationKeys } from '@/hooks/api/useOrganizations';

/**
 * Query key factory for workspaces
 */
export const workspaceKeys = {
  all: ['workspaces'] as const,
  lists: () => [...workspaceKeys.all, 'list'] as const,
  list: (filters: WorkspaceFilters) => [...workspaceKeys.lists(), filters] as const,
  details: () => [...workspaceKeys.all, 'detail'] as const,
  detail: (id: string) => [...workspaceKeys.details(), id] as const,
  members: (id: string, filters?: MemberFilters) =>
    [...workspaceKeys.detail(id), 'members', filters] as const,
  projects: (id: string, filters?: ProjectFilters) =>
    [...workspaceKeys.detail(id), 'projects', filters] as const,
};

/**
 * Hook to fetch paginated list of workspaces
 */
export function useWorkspaces(filters: WorkspaceFilters) {
  return useQuery({
    queryKey: workspaceKeys.list(filters),
    queryFn: () => workspacesApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch a single workspace by ID
 */
export function useWorkspace(id: string) {
  return useQuery({
    queryKey: workspaceKeys.detail(id),
    queryFn: () => workspacesApi.getById(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch members for a workspace
 */
export function useWorkspaceMembers(id: string, filters?: MemberFilters) {
  return useQuery({
    queryKey: workspaceKeys.members(id, filters),
    queryFn: () => workspacesApi.getMembers(id, filters),
    enabled: !!id,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch projects for a workspace
 */
export function useWorkspaceProjects(id: string, organizationId: string, filters?: ProjectFilters) {
  return useQuery({
    queryKey: workspaceKeys.projects(id, filters),
    queryFn: () => workspacesApi.getProjects(id, organizationId, filters),
    enabled: !!id && !!organizationId,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });
}

/**
 * Hook to restore a soft-deleted workspace
 */
export function useRestoreWorkspace() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, organizationId }: { id: string; organizationId: string }) =>
      workspacesApi.restore(id, organizationId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Workspace restored successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to restore workspace',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to add a member to a workspace
 */
export function useAddWorkspaceMember() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, userId, role }: { id: string; userId: string; role: string }) =>
      workspacesApi.addMember(id, userId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Member added successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to add member',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to remove a member from a workspace
 */
export function useRemoveWorkspaceMember() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      workspacesApi.removeMember(id, userId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to remove member',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update a member's role in a workspace
 */
export function useUpdateWorkspaceMemberRole() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, userId, role }: { id: string; userId: string; role: string }) =>
      workspacesApi.updateMemberRole(id, userId, role),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(variables.id) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.id) });
      toast({
        title: 'Success',
        description: 'Member role updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to update member role',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to create a new workspace within an organization
 */
export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      organizationId,
      payload,
    }: {
      organizationId: string;
      payload: CreateWorkspacePayload;
    }) => workspacesApi.create(organizationId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.lists() });
      // Invalidate the organization detail and its workspaces sub-queries
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.organizationId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.workspaces(variables.organizationId),
      });
      toast({
        title: 'Success',
        description: 'Workspace created successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to create workspace',
        variant: 'destructive',
      });
    },
  });
}
